import { NotificationSnackBar } from "@/controls/snackbars/notification-snackbar";
import { routeAdjustmentManager } from "@/setup/routes/adjustment.routes";
import { masterDetailsMinHeight, pageBgColor } from "@/utils/theme";
import {
	ErrorModal,
	HelmetDexteel,
	ShiftDayNavigatorControl,
	useShiftNavigator,
} from "@dexteel/mesf-core";
import { Divider, Grid2, Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { get } from "lodash-es";
import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { AdjustmentsManagerTable } from "./components/table/AdjustmentsManagerTable";
import { MillAdjustmentByShift } from "./models/MillAdjustmentByShift";
import { getAdjustmentsByShift } from "./repositories/AdjustmentManagerRepository";

type useAdjustmentsByShiftProps = {
	shiftId?: number | undefined;
	millAdjustmentId?: number | null;
};

export const useAdjustmentsByShift = ({
	shiftId,
	millAdjustmentId,
}: useAdjustmentsByShiftProps) => {
	return useQuery<MillAdjustmentByShift[], Error>({
		queryKey: ["adjustments", millAdjustmentId, shiftId],
		queryFn: ({ signal }) =>
			getAdjustmentsByShift(
				{
					shiftId: shiftId || null,
					millAdjustmentId,
				},
				signal,
			),
		enabled: !!shiftId || !!millAdjustmentId || millAdjustmentId === null,
	});
};

type Props = {};

export const AdjustmentsManagerPage = (props: Props) => {
	const [error, setError] = useState("");
	const [notificationMessage, setNotificationMessage] = useState("");

	const navigate = useNavigate();
	const { shiftId: shiftIdParam } = useParams();
	const [params] = useSearchParams();
	const { shiftInfo, getShiftDataFromAPI } = useShiftNavigator();
	const adjustmentId = params.get("adjustmentId");
	const assetId = Number(params.get("assetId")) || null;

	const {
		data: adjustments,
		refetch,
		isLoading,
		error: e,
		isError,
	} = useAdjustmentsByShift({
		shiftId: shiftInfo?.CurrentShiftId,
	});

	useEffect(() => {
		if (shiftInfo) {
			const shiftId = get(shiftInfo, "CurrentShiftId", null);
			if (adjustmentId) {
				navigate(
					`${routeAdjustmentManager}/${shiftId}?adjustmentId=${adjustmentId}`,
				);
			} else navigate(`${routeAdjustmentManager}/${shiftId}`);
			(async () => {
				await refetch();
			})();
		}
	}, [shiftInfo, assetId]);

	useEffect(() => {
		if (shiftIdParam) {
			(async () => {
				const shiftId = Number(shiftIdParam);
				await getShiftDataFromAPI(null, shiftId);
			})();
		} else {
			(async () => {
				await getShiftDataFromAPI(null, 0);
			})();
		}
	}, []);

	useEffect(() => {
		if (e && isError) {
			setError(e.message);
		}
	}, [e, isError]);

	return (
		<>
			<HelmetDexteel title="Adjustments Manager" />
			<Grid2
				container
				size={{ md: 12, xs: 12 }}
				spacing={1}
				px={2}
				pt={2}
				sx={{ backgroundColor: pageBgColor, minHeight: "calc(100vh - 160px)" }}
			>
				<Grid2 size={{ md: 12, xs: 12 }}>
					<Typography variant="h5" style={{ fontWeight: 600 }}>
						Adjustments Manager
					</Typography>
				</Grid2>
				<Grid2 maxWidth="fit-content">
					<ShiftDayNavigatorControl preventInit />
				</Grid2>
				<Grid2 size={{ md: 12 }}>
					<Divider />
				</Grid2>
				<Grid2>
					<AdjustmentsManagerTable
						adjustmentId={adjustmentId}
						isLoading={isLoading}
						rows={adjustments || []}
					/>
				</Grid2>
			</Grid2>
			<ErrorModal error={error} onHide={() => setError("")} />
			<NotificationSnackBar
				message={notificationMessage}
				onHide={() => setNotificationMessage("")}
			/>
		</>
	);
};
